/**
=========================================================
* TripEnhancer - ArticleLandingPage Component
=========================================================
* 
* Component that handles article display with smart deep linking
* - Attempts to open in app if installed
* - Shows web preview with install prompt if app not installed
* - Supports multilingual content (EN/NL)
*/

import { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

// @mui icons
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AndroidIcon from "@mui/icons-material/Android";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Image resolver for optimized image handling
import { resolveImageUrl, preloadImage } from "lib/imageResolver";

function ArticleLandingPage({ article }) {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [language, setLanguage] = useState("en");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentParagraph, setCurrentParagraph] = useState(-1);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState(article.image);
  const utteranceRef = useRef(null);

  // Detect browser language first and optimize image
  useEffect(() => {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith("nl")) {
      setLanguage("nl");
    }

    if ("speechSynthesis" in window) {
      setSpeechSupported(true);
    }

    // Resolve and cache the image URL (uses local cache if available)
    const optimizedUrl = resolveImageUrl(article.image, 1280);
    setOptimizedImageUrl(optimizedUrl);

    // Preload the image for better performance
    preloadImage(article.image).catch((err) => {
      console.warn("Failed to preload image:", err);
    });
  }, [article.image]);

  const title = language === "nl" && article.titleNL ? article.titleNL : article.title;
  const excerpt = language === "nl" && article.excerptNL ? article.excerptNL : article.excerpt;
  const content = language === "nl" && article.contentNL ? article.contentNL : article.content;

  // Text-to-Speech Functions - wrapped in useCallback for proper dependencies
  const speakArticle = useCallback(() => {
    if (!speechSupported) {
      console.log("Speech not supported");
      return;
    }

    // Cancel any existing speech
    window.speechSynthesis.cancel();

    // Wait for voices to be loaded
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      console.log("Voices not loaded yet, waiting...");
      // Wait a bit and try again
      setTimeout(() => speakArticle(), 100);
      return;
    }

    const fullText = `${title}. ${excerpt}. ${content}`;
    console.log("Starting speech:", fullText.substring(0, 50) + "...");

    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.lang = language === "nl" ? "nl-NL" : "en-US";
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utteranceRef.current = utterance;

    utterance.onstart = () => {
      console.log("Speech started");
      setIsPlaying(true);
      setIsPaused(false);
    };

    utterance.onboundary = (event) => {
      if (event.name === "sentence") {
        const spokenText = fullText.substring(0, event.charIndex);
        const paragraphsSoFar = spokenText.split("\n\n").length - 1;
        setCurrentParagraph(paragraphsSoFar);
      }
    };

    utterance.onend = () => {
      console.log("Speech ended");
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentParagraph(-1);
    };

    utterance.onerror = (event) => {
      console.error("Speech error:", event);
      console.error("Error type:", event.error);

      // Common error: "not-allowed" means browser blocked autoplay
      if (event.error === "not-allowed") {
        console.log("🔇 Browser blocked autoplay. User needs to click Play button.");
        // Reset state so user can click play button
        setIsPlaying(false);
        setIsPaused(false);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentParagraph(-1);
      }
    };

    try {
      window.speechSynthesis.speak(utterance);
      console.log("Speech queued");
      setIsPlaying(true);
      setIsPaused(false);
    } catch (error) {
      console.error("Error speaking:", error);
      setIsPlaying(false);
      setIsPaused(false);
    }
  }, [speechSupported, title, excerpt, content, language]);

  const pauseSpeech = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resumeSpeech = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else if (isPaused && utteranceRef.current) {
      speakArticle();
    }
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentParagraph(-1);
  };

  const togglePlayPause = () => {
    if (!isPlaying) {
      speakArticle();
    } else if (isPaused) {
      resumeSpeech();
    } else {
      pauseSpeech();
    }
  };

  useEffect(() => {
    // Load voices for speech synthesis
    if ("speechSynthesis" in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        console.log("Voices loaded:", voices.length);
      };

      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    const appTimeout = setTimeout(() => {
      setShowInstallPrompt(true);

      // Try autoplay, but it will likely be blocked by browser policy
      // User will see the Play button prominently displayed
      if (speechSupported) {
        console.log("Attempting autoplay (may be blocked by browser)...");
        // Attempt autoplay - if browser allows, great! If not, user clicks play
        const attemptAutoplay = async () => {
          try {
            // Small delay to ensure page has settled
            await new Promise((resolve) => setTimeout(resolve, 100));
            speakArticle();
          } catch (error) {
            console.log("Autoplay blocked, waiting for user interaction:", error);
          }
        };
        attemptAutoplay();
      }
    }, 2000);

    // DON'T use window.location.href - it breaks speech synthesis autoplay
    // The "Open in App" button provides the app linking functionality

    // Listen for visibility change (user came back = app didn't open)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setShowInstallPrompt(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(appTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Stop any ongoing speech when component unmounts
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [article.id, speechSupported, speakArticle]);

  const texts = {
    en: {
      opening: "Opening in TripEnhancer...",
      installTitle: "Get the Full Experience",
      installDescription: "Listen to audio-guided tours with TripEnhancer",
      downloadButton: "Download App",
      openInApp: "Open in App",
      readMore: "Read More",
      location: "Location",
      aboutApp: "About TripEnhancer",
      appDescription:
        "Discover stories about landmarks around you with audio-guided tours. Perfect for walking, cycling, or traveling.",
      features: "Features",
      audioGuided: "Audio-Guided Tours",
      audioGuidedDesc: "Listen to expert narration about landmarks",
      gpsEnabled: "GPS-Enabled",
      gpsEnabledDesc: "Real-time narration at each point of interest",
      multilingual: "Multilingual",
      multilingualDesc: "Available in English and Dutch",
      play: "Play Audio Tour",
      pause: "Pause",
      resume: "Resume",
      stop: "Stop",
      listenToArticle: "🎧 Click Play to start the audio tour",
    },
    nl: {
      opening: "Openen in TripEnhancer...",
      installTitle: "Krijg de Volledige Ervaring",
      installDescription: "Luister naar audiogeleide tours met TripEnhancer",
      downloadButton: "Download App",
      openInApp: "Open in App",
      readMore: "Lees Meer",
      location: "Locatie",
      aboutApp: "Over TripEnhancer",
      appDescription:
        "Ontdek verhalen over bezienswaardigheden om je heen met audiogeleide tours. Perfect voor wandelen, fietsen of reizen.",
      features: "Functies",
      audioGuided: "Audiogeleide Tours",
      audioGuidedDesc: "Luister naar expert verhalen over bezienswaardigheden",
      gpsEnabled: "GPS-Ondersteund",
      gpsEnabledDesc: "Real-time vertelling bij elk interessant punt",
      multilingual: "Meertalig",
      multilingualDesc: "Beschikbaar in Engels en Nederlands",
      play: "▶️ Audiotour Afspelen",
      pause: "Pauzeren",
      resume: "Hervatten",
      stop: "Stoppen",
      listenToArticle: "🎧 Klik op Afspelen om naar dit artikel te luisteren",
    },
  };

  const t = texts[language];

  return (
    <>
      <Helmet>
        <title>{`${title} - TripEnhancer`}</title>
        <meta name="description" content={excerpt} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={excerpt} />
        <meta property="og:image" content={optimizedImageUrl} />
        <meta property="og:url" content={`https://tripenhancer.app/article/${article.id}`} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={excerpt} />
        <meta name="twitter:image" content={optimizedImageUrl} />
        {/* Canonical */}
        <link rel="canonical" href={`https://tripenhancer.app/article/${article.id}`} />
      </Helmet>

      <MKBox component="section" py={2}>
        {/* Hero Section with Image */}
        <MKBox
          position="relative"
          minHeight="50vh"
          width="100%"
          sx={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${optimizedImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <Container>
            <Grid container>
              <Grid item xs={12} lg={8}>
                <MKBox pb={6}>
                  <MKTypography
                    variant="h1"
                    color="white"
                    mb={1}
                    sx={({ breakpoints, typography: { size } }) => ({
                      [breakpoints.down("md")]: {
                        fontSize: size["3xl"],
                      },
                    })}
                  >
                    {title}
                  </MKTypography>
                  <MKTypography variant="h5" color="white" opacity={0.9}>
                    {article.location.city}
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Loading/Install Prompt */}
        {!showInstallPrompt && (
          <Container>
            <MKBox py={8} textAlign="center">
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <MKTypography variant="h5" color="text">
                {t.opening}
              </MKTypography>
            </MKBox>
          </Container>
        )}

        {/* Install Prompt Banner */}
        {showInstallPrompt && (
          <MKBox
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1000,
              background: "linear-gradient(90deg, #1565c0 0%, #1976d2 100%)",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <Container>
              <MKBox py={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <MKBox display="flex" alignItems="center" gap={2}>
                      <MKBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AndroidIcon sx={{ fontSize: 32, color: "#1976d2" }} />
                      </MKBox>
                      <MKBox>
                        <MKTypography variant="h6" color="white" fontWeight="bold">
                          {t.installTitle}
                        </MKTypography>
                        <MKTypography variant="body2" color="white" opacity={0.9}>
                          {t.installDescription}
                        </MKTypography>
                      </MKBox>
                    </MKBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MKBox
                      display="flex"
                      gap={1}
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    >
                      <Button
                        variant="contained"
                        href={`tripenhancer://article/${article.id}`}
                        sx={{
                          background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          border: "2px solid #ff8c00",
                          boxShadow: "0 4px 12px rgba(255,107,53,0.4)",
                          textTransform: "none",
                          px: 3,
                          py: 1.5,
                          "&:hover": {
                            background: "linear-gradient(135deg, #ff8c00 0%, #ffa500 100%)",
                            borderColor: "#ff6b35",
                            boxShadow: "0 6px 16px rgba(255,107,53,0.6)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                        startIcon={<OpenInNewIcon sx={{ fontSize: 24 }} />}
                      >
                        {t.openInApp}
                      </Button>
                      <Button
                        variant="contained"
                        href="https://play.google.com/store/apps/details?id=com.naxx.tripenhancer.free"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.95rem",
                          boxShadow: "0 3px 10px rgba(102,126,234,0.3)",
                          textTransform: "none",
                          px: 3,
                          py: 1.5,
                          "&:hover": {
                            background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                            boxShadow: "0 5px 14px rgba(102,126,234,0.5)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                        startIcon={<DownloadIcon sx={{ fontSize: 22 }} />}
                      >
                        {t.downloadButton}
                      </Button>
                    </MKBox>
                  </Grid>
                </Grid>
              </MKBox>
            </Container>
          </MKBox>
        )}

        {/* Article Content */}
        <Container>
          <Grid container justifyContent="center" sx={{ mt: 4, mb: 4 }}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ p: { xs: 3, md: 5 }, mb: 4 }}>
                <MKTypography variant="h4" color="text" mb={3}>
                  {excerpt}
                </MKTypography>

                {/* Audio Player Controls */}
                {speechSupported && (
                  <MKBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <VolumeUpIcon sx={{ color: "#1976d2", fontSize: 28 }} />
                    <MKTypography variant="body2" color="text" sx={{ flex: 1, minWidth: "150px" }}>
                      {t.listenToArticle}
                    </MKTypography>
                    <MKBox display="flex" gap={1}>
                      <MKButton
                        variant="gradient"
                        color={isPlaying && !isPaused ? "error" : "info"}
                        size="small"
                        onClick={togglePlayPause}
                        sx={{ minWidth: "100px" }}
                      >
                        {!isPlaying ? (
                          <>
                            <PlayArrowIcon sx={{ mr: 0.5 }} />
                            {t.play}
                          </>
                        ) : isPaused ? (
                          <>
                            <PlayArrowIcon sx={{ mr: 0.5 }} />
                            {t.resume}
                          </>
                        ) : (
                          <>
                            <PauseIcon sx={{ mr: 0.5 }} />
                            {t.pause}
                          </>
                        )}
                      </MKButton>
                      {(isPlaying || isPaused) && (
                        <MKButton variant="outlined" color="dark" size="small" onClick={stopSpeech}>
                          <StopIcon sx={{ mr: 0.5 }} />
                          {t.stop}
                        </MKButton>
                      )}
                    </MKBox>
                  </MKBox>
                )}

                <Divider sx={{ my: 3 }} />

                <MKBox>
                  {content.split("\n\n").map((paragraph, index) => (
                    <MKTypography
                      key={index}
                      variant="body1"
                      color="text"
                      mb={2}
                      sx={{
                        lineHeight: 1.8,
                        textAlign: "justify",
                        backgroundColor:
                          isPlaying && currentParagraph === index ? "#fff9c4" : "transparent",
                        padding: isPlaying && currentParagraph === index ? "8px" : "0",
                        borderRadius: isPlaying && currentParagraph === index ? "4px" : "0",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {paragraph}
                    </MKTypography>
                  ))}
                </MKBox>

                {/* Location Info */}
                <Divider sx={{ my: 4 }} />
                <MKBox>
                  <MKTypography
                    variant="h6"
                    color="text"
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <LocationOnIcon color="primary" />
                    {t.location}
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    {article.location.city} ({article.location.lat.toFixed(4)},{" "}
                    {article.location.lng.toFixed(4)})
                  </MKTypography>
                </MKBox>
              </Card>

              {/* About TripEnhancer */}
              <Card
                sx={{
                  p: { xs: 3, md: 5 },
                  background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)",
                }}
              >
                <MKTypography variant="h3" color="dark" mb={2}>
                  {t.aboutApp}
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={4}>
                  {t.appDescription}
                </MKTypography>

                <Grid container spacing={3} mb={4}>
                  <Grid item xs={12} md={4}>
                    <MKBox textAlign="center">
                      <MKBox
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          borderRadius: "50%",
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 16px",
                        }}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z" />
                        </svg>
                      </MKBox>
                      <MKTypography variant="h6" color="dark" mb={1}>
                        {t.audioGuided}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {t.audioGuidedDesc}
                      </MKTypography>
                    </MKBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MKBox textAlign="center">
                      <MKBox
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          borderRadius: "50%",
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 16px",
                        }}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm-2 13l-4-4 1.4-1.4L10 12.2l6.6-6.6L18 7l-8 8z" />
                        </svg>
                      </MKBox>
                      <MKTypography variant="h6" color="dark" mb={1}>
                        {t.gpsEnabled}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {t.gpsEnabledDesc}
                      </MKTypography>
                    </MKBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MKBox textAlign="center">
                      <MKBox
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          borderRadius: "50%",
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 16px",
                        }}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
                        </svg>
                      </MKBox>
                      <MKTypography variant="h6" color="dark" mb={1}>
                        {t.multilingual}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {t.multilingualDesc}
                      </MKTypography>
                    </MKBox>
                  </Grid>
                </Grid>

                <MKBox display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                  <MKButton
                    variant="gradient"
                    color="info"
                    size="large"
                    href="https://play.google.com/store/apps/details?id=com.naxx.tripenhancer.free"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: "bold",
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    Google Play
                  </MKButton>
                </MKBox>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MKBox>
    </>
  );
}

ArticleLandingPage.propTypes = {
  article: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    titleNL: PropTypes.string,
    excerpt: PropTypes.string.isRequired,
    excerptNL: PropTypes.string,
    content: PropTypes.string.isRequired,
    contentNL: PropTypes.string,
    image: PropTypes.string.isRequired,
    location: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      city: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default ArticleLandingPage;
